// src/utils/preferenceMappers.js

/**
 * Formats a time string from HH:MM to HH:MM:SS.
 * If already HH:MM:SS, returns as is.
 * @param {string} timeStr - The time string (e.g., "09:00" or "09:00:00")
 * @returns {string} Formatted time string "HH:MM:SS" or original if not parsable.
 */
const formatTimeToHhMmSs = (timeStr) => {
    if (timeStr && /^\d{2}:\d{2}$/.test(timeStr)) { // Checks for HH:MM format
        return `${timeStr}:00`;
    }
    if (timeStr && /^\d{2}:\d{2}:\d{2}$/.test(timeStr)) { // Already HH:MM:SS
        return timeStr;
    }
    // Log unexpected format but return original to let API handle if it's some other valid format
    // or if it's null/undefined.
    if (timeStr !== undefined && timeStr !== null) { // Avoid logging for undefined/null which might be legitimate
      console.warn(`[preferenceMappers.formatTimeToHhMmSs] Unexpected time format encountered: ${timeStr}. Expected HH:MM or HH:MM:SS.`);
    }
    return timeStr;
};

/**
 * Converts a frontend preference object to the format for the Group Preferences API.
 * Frontend: { day, startTime: "HH:MM", endTime: "HH:MM", status }
 * API:      { day, start_time: "HH:MM:SS", end_time: "HH:MM:SS", status }
 */
export const mapPreferenceToGroupApiFormat = (frontendPref) => {
    if (!frontendPref || typeof frontendPref !== 'object') {
        console.error("Invalid frontend preference object for Group API mapping:", frontendPref);
        return frontendPref; // Return as is or throw error
    }
    return {
        // id: frontendPref.id, // Only include ID if your API uses it for updates on PUT /preferences
        day: frontendPref.day,
        start_time: formatTimeToHhMmSs(frontendPref.startTime),
        end_time: formatTimeToHhMmSs(frontendPref.endTime),
        status: frontendPref.status,
    };
};

/**
 * Converts a frontend preference object to the format for the Teacher Preferences API.
 * Frontend: { day, startTime: "HH:MM", endTime: "HH:MM", status }
 * API:      { day, start_time: "HH:MM:SS", end_time: "HH:MM:SS", status } (keys are snake_case)
 */
export const mapPreferenceToTeacherApiFormat = (frontendPref) => {
    if (!frontendPref || typeof frontendPref !== 'object') {
        console.error("Invalid frontend preference object for Teacher API mapping:", frontendPref);
        return frontendPref; // Return as is or throw error
    }
    
    const result = {
        day: frontendPref.day,
        start_time: formatTimeToHhMmSs(frontendPref.startTime), // Convert to snake_case
        end_time: formatTimeToHhMmSs(frontendPref.endTime),     // Convert to snake_case
        status: frontendPref.status,
    };
    
    console.log('[preferenceMappers] Frontend to API transformation:', {
        input: frontendPref,
        output: result
    });
    
    return result;
};

/**
 * Converts a preference object from the API (potentially snake_case keys) to frontend format (camelCase keys).
 * API:      { id?, day, start_time?, startTime?, end_time?, endTime?, status, createdAt?, updatedAt? }
 * Frontend: { id?, day, startTime, endTime, status, createdAt?, updatedAt? }
 */
export const mapPreferenceFromApiFormat = (apiPref) => {
    if (!apiPref || typeof apiPref !== 'object') {
        console.error("Invalid API preference object for frontend mapping:", apiPref);
        return apiPref; // Return as is or throw error
    }
    
    const result = {
        id: apiPref.id,
        day: apiPref.day,
        // Prioritize snake_case from API, fallback to camelCase if API is inconsistent or already mapped
        startTime: apiPref.start_time || apiPref.startTime,
        endTime: apiPref.end_time || apiPref.endTime,
        status: apiPref.status,
        createdAt: apiPref.createdAt || apiPref.created_at,
        updatedAt: apiPref.updatedAt || apiPref.updated_at,
    };
    
    console.log('[preferenceMappers] API to Frontend transformation:', {
        input: apiPref,
        output: result
    });
    
    return result;
};